import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BiArrowBack,
  BiPause,
  BiPlay,
  BiRun,
  BiStop,
  BiEdit,
  BiDotsVerticalRounded,
} from 'react-icons/bi'
import {
  selectScheduledTasks,
  selectTaskRuns,
  fetchTaskRuns,
  fetchTaskRunDetail,
  deleteScheduledTask,
  toggleScheduledTask,
  runScheduledTask,
  selectTaskRunsStatus,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import ConfirmModal from './ConfirmModal'
import TaskInstructionsCard from './TaskInstructionsCard'
import TaskPlanSteps from './TaskPlanSteps'
import RunHistorySection from './RunHistorySection'
import {
  formatOnceTaskTiming,
  isOnceTask,
  nextRunRelative,
  onceTaskCompleted,
  onceTaskStatusBadge,
  scheduleLabel,
  triggerLabel,
} from '../utils/scheduledTaskDisplay'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shadcn/ui/dropdown-menu'

/** Shadcn dropdown forwardRef typings are incomplete in this project */
const TaskOverflowContent = DropdownMenuContent as any
const TaskOverflowItem = DropdownMenuItem as any

interface ScheduledTaskDetailProps {
  taskId: string
  onBack: () => void
  onEditTask?: (task: any) => void
  /** When true (mobile shell): header shows primary + ⋮ only; secondaries live in the menu. */
  compactActionBar?: boolean
  /** e.g. from email deep link `?runId=` — expands this run once shown */
  initialExpandedRunId?: string | null
}

export default function ScheduledTaskDetail({
  taskId,
  onBack,
  onEditTask,
  compactActionBar = true,
  initialExpandedRunId = null,
}: ScheduledTaskDetailProps) {
  const dispatch = useDispatch()
  const tasks = useSelector(selectScheduledTasks)
  const { runs } = useSelector((state: any) => selectTaskRuns(state, taskId))
  const runsStatus = useSelector(selectTaskRunsStatus)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [runDetailsById, setRunDetailsById] = useState<Record<string, any>>({})
  const [deleting, setDeleting] = useState(false)
  const [running, setRunning] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const runAbortRef = React.useRef<AbortController | null>(null)
  const prevTaskIdRef = React.useRef<string | null>(null)
  const autoSelectedRunningRunRef = React.useRef(false)

  const task = Array.isArray(tasks) ? tasks.find((t: any) => t.id === taskId) : null

  const truncateForToastTitle = (name: string, max = 52) => {
    const n = String(name || '').trim() || 'This scheduled task'
    return n.length <= max ? n : `${n.slice(0, max - 1)}…`
  }

  useEffect(() => {
    if (taskId) dispatch(fetchTaskRuns({ taskId, limit: 50, offset: 0 }) as any)
  }, [dispatch, taskId])

  useEffect(() => {
    if (prevTaskIdRef.current !== null && prevTaskIdRef.current !== taskId) {
      setSelectedRunId(null)
      setRunDetailsById({})
    }
    prevTaskIdRef.current = taskId
  }, [taskId])

  // POST /run holds the connection until execution finishes; the run row exists in DB as "running" long before that.
  // Poll so run history updates without a full page refresh.
  useEffect(() => {
    if (!running || !taskId) return
    const poll = () => {
      dispatch(fetchTaskRuns({ taskId, limit: 50, offset: 0, silent: true }) as any)
    }
    poll()
    const intervalId = window.setInterval(poll, 2500)
    return () => window.clearInterval(intervalId)
  }, [running, taskId, dispatch])

  // Ref to read latest runs without putting runs in deps (selector can return new ref each time and cause loop)
  const runsRef = React.useRef(runs)
  runsRef.current = runs

  const firstRunId = Array.isArray(runs) && runs[0] ? runs[0].id : null
  const runningRunId = Array.isArray(runs)
    ? runs.find((r: any) => r.status === 'running')?.id ?? null
    : null
  const selectedListStatus = Array.isArray(runs)
    ? runs.find((r: any) => r.id === selectedRunId)?.status ?? null
    : null

  useEffect(() => {
    if (!running) {
      autoSelectedRunningRunRef.current = false
    }
  }, [running])

  // Default to the newest run; honor email deep links; auto-select an in-progress manual run.
  useEffect(() => {
    if (running && runningRunId && !autoSelectedRunningRunRef.current) {
      setSelectedRunId(runningRunId)
      autoSelectedRunningRunRef.current = true
      return
    }
    setSelectedRunId((current) => {
      const list = runsRef.current
      if (current && Array.isArray(list) && list.some((r: any) => r.id === current)) return current
      if (
        initialExpandedRunId &&
        Array.isArray(list) &&
        list.some((r: any) => r.id === initialExpandedRunId)
      ) {
        return initialExpandedRunId
      }
      return firstRunId
    })
  }, [firstRunId, runningRunId, running, initialExpandedRunId, taskId])

  const runDetailsByIdRef = React.useRef(runDetailsById)
  runDetailsByIdRef.current = runDetailsById

  useEffect(() => {
    if (!selectedRunId) return
    const cached = runDetailsByIdRef.current[selectedRunId]
    if (cached?.response_data) return
    const listRun = Array.isArray(runsRef.current)
      ? runsRef.current.find((r: any) => r.id === selectedRunId)
      : null
    if (listRun?.response_data) {
      setRunDetailsById((prev) =>
        prev[selectedRunId]?.response_data ? prev : { ...prev, [selectedRunId]: listRun },
      )
      return
    }
    let cancelled = false
    dispatch(fetchTaskRunDetail({ taskId, runId: selectedRunId }) as any).then((action: any) => {
      if (cancelled) return
      if (action?.payload?.run) {
        setRunDetailsById((prev) => ({ ...prev, [selectedRunId]: action.payload.run }))
      }
    })
    return () => {
      cancelled = true
    }
  }, [selectedRunId, selectedListStatus, dispatch, taskId])

  const mergedSelectedRun = useMemo(() => {
    if (!selectedRunId) return null
    const listRun = Array.isArray(runs) ? runs.find((r: any) => r.id === selectedRunId) : null
    const cached = runDetailsById[selectedRunId]
    if (!listRun && !cached) return null
    return {
      ...(listRun || {}),
      ...(cached || {}),
      status: listRun?.status ?? cached?.status,
      error_message: listRun?.error_message ?? cached?.error_message,
      response_data: cached?.response_data || listRun?.response_data,
      resolved_parameters: cached?.resolved_parameters || listRun?.resolved_parameters,
    }
  }, [runs, selectedRunId, runDetailsById])

  const handleDeleteClick = () => setDeleteConfirmOpen(true)

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false)
    setDeleting(true)
    try {
      await dispatch(deleteScheduledTask(taskId) as any).unwrap()
      toast.success('Task deleted')
      onBack()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  const handleToggle = async () => {
    if (!task) return
    const wasActive = !!task.is_active
    const title = truncateForToastTitle(task.name)
    const sched = String(scheduleLabel(task) || 'scheduled').toLowerCase()
    try {
      await dispatch(toggleScheduledTask(taskId) as any).unwrap()
      toast.success(
        wasActive
          ? `"${title}" is paused. It will not run on its ${sched} schedule until you resume it.`
          : `"${title}" is active again and will run on its ${sched} schedule.`
      )
    } catch (e: any) {
      const detail =
        typeof e === 'string'
          ? e
          : e?.message || e?.error || (e?.data && String(e.data?.error || e.data))
      toast.error(
        detail ? `Could not update "${title}": ${detail}` : `Could not update "${title}". Try again.`
      )
    }
  }

  const handleRunNow = async () => {
    // Ref is synchronous; `running` state is not — blocks double-submit before re-render.
    if (runAbortRef.current) return
    const ac = new AbortController()
    runAbortRef.current = ac
    setRunning(true)
    dispatch(fetchTaskRuns({ taskId, limit: 50, offset: 0, silent: true }) as any)
    try {
      await dispatch(runScheduledTask({ taskId, manual: true, signal: ac.signal }) as any).unwrap()
      toast.success('Run completed')
    } catch (e: any) {
      if (e?.cancelled || e?.payload?.cancelled) {
        toast.info('Run stopped')
      } else if (e?.already_running === true || e?.status === 409 || e?.response?.status === 409) {
        toast.info('This task is already running')
        dispatch(fetchTaskRuns({ taskId, limit: 50, offset: 0, silent: true }) as any)
      } else {
        const msg =
          typeof e === 'string'
            ? e
            : e?.error || e?.message || (e?.data && String(e.data?.error || e.data)) || 'Run failed'
        toast.error(msg)
      }
    } finally {
      runAbortRef.current = null
      setRunning(false)
      dispatch(fetchTaskRuns({ taskId, limit: 50, offset: 0, silent: true }) as any)
    }
  }

  const handleStopRun = () => {
    runAbortRef.current?.abort()
  }

  /**
   * Pipeline steps for the execution-plan review cards.
   * Hook must run before any early return (see toolNames note historically).
   */
  const planSteps = useMemo(() => {
    if (Array.isArray(task?.tools) && task.tools.length > 0) return task.tools
    if (task?.tool_name) {
      return [
        {
          tool_name: task.tool_name,
          tool_parameters: task.tool_parameters || {},
          execution_order: 1,
          depends_on: null,
          output_mapping: null,
        },
      ]
    }
    return []
  }, [task])

  if (!task) {
    return (
      <div className="tw-p-8 tw-text-center tw-text-slate-500">
        <p>Task not found.</p>
        <button
          type="button"
          onClick={onBack}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3.5 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors"
        >
          <BiArrowBack className="tw-w-4 tw-h-4 tw-shrink-0" /> Back
        </button>
      </div>
    )
  }

  const headerGhostBtn =
    'tw-inline-flex tw-h-9 tw-shrink-0 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-slate-800 tw-shadow-sm tw-cursor-pointer tw-transition-colors hover:tw-border-slate-300 hover:tw-bg-slate-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50'
  const headerPrimaryBtn =
    'tw-inline-flex tw-h-9 tw-shrink-0 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-bg-slate-900 tw-px-3 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm tw-cursor-pointer tw-transition-colors hover:tw-bg-slate-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-50'
  const overflowTriggerBtn =
    'tw-inline-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 tw-shadow-sm tw-cursor-pointer tw-transition-colors hover:tw-border-slate-300 hover:tw-bg-slate-50'
  /** Wider viewports: Run now / Edit / Stop inline; they move into ⋮ below `lg`. Mobile shell: always in ⋮. */
  const secondaryInlineClass = compactActionBar ? 'tw-hidden' : 'tw-hidden lg:tw-inline-flex'
  const overflowDuplicateClass = compactActionBar ? '' : 'lg:tw-hidden'
  const schedulePills = (
    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
      <span className="tw-inline-flex tw-rounded-full tw-bg-sky-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-sky-900 tw-ring-1 tw-ring-sky-100">
        {triggerLabel(task)}
      </span>
      <span className="tw-inline-flex tw-rounded-full tw-bg-slate-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-700">
        {scheduleLabel(task)}
      </span>
      {task.delivery_method && (
        <span className="tw-inline-flex tw-rounded-full tw-bg-violet-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-violet-900 tw-ring-1 tw-ring-violet-100">
          Delivery: {String(task.delivery_method).replace(/_/g, ' ')}
        </span>
      )}
      {!isOnceTask(task) && task.is_active && task.next_run_at && (
        <span className="tw-inline-flex tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-emerald-800 tw-ring-1 tw-ring-emerald-100">
          Next run {nextRunRelative(task.next_run_at)}
        </span>
      )}
      {task.last_run_at && (
        <span className="tw-inline-flex tw-rounded-full tw-bg-slate-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-600 tw-ring-1 tw-ring-slate-200">
          Last run {new Date(task.last_run_at).toLocaleString()}
        </span>
      )}
    </div>
  )

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-min-w-0 tw-bg-white">
      <div className="tw-border-b tw-border-slate-200 tw-px-3 tw-pt-2 tw-pb-3 sm:tw-px-4 sm:tw-pt-3 sm:tw-pb-4">
        <button
          type="button"
          className="tw-mb-2 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-2 tw-text-sm tw-font-medium tw-text-slate-600 tw-cursor-pointer hover:tw-bg-slate-100 tw-transition-colors tw--ml-2"
          onClick={onBack}
          title="Back to chat"
        >
          <BiArrowBack className="tw-w-5 tw-h-5 tw-shrink-0" />
          Back
        </button>

        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
          <h1 className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-left tw-text-xl tw-font-medium tw-text-slate-900 tw-leading-snug tw-break-words">
            {task.name}
          </h1>
          <div className="tw-flex tw-min-w-0 tw-w-full tw-flex-wrap tw-items-center tw-gap-2 sm:tw-w-auto sm:tw-justify-end">
            {!isOnceTask(task) && (
              <button
                type="button"
                onClick={handleToggle}
                disabled={running}
                className={`${headerPrimaryBtn} ${compactActionBar ? 'tw-min-w-0 tw-flex-1 sm:tw-flex-initial' : ''}`}
              >
                {task.is_active ? (
                  <BiPause className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
                ) : (
                  <BiPlay className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
                )}
                {task.is_active ? 'Pause' : 'Resume'}
              </button>
            )}
            <button
              type="button"
              onClick={handleRunNow}
              disabled={running}
              className={`${isOnceTask(task) ? headerPrimaryBtn : headerGhostBtn} ${secondaryInlineClass}`}
            >
              <BiRun className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
              {running ? 'Running…' : 'Run now'}
            </button>
            {running && (
              <button
                type="button"
                onClick={handleStopRun}
                className={`${headerGhostBtn} ${secondaryInlineClass}`}
              >
                <BiStop className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
                Stop
              </button>
            )}
            {onEditTask && (
              <button
                type="button"
                onClick={() => onEditTask(task)}
                className={`${headerGhostBtn} ${secondaryInlineClass}`}
              >
                <BiEdit className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
                Edit
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={overflowTriggerBtn} aria-label="More actions">
                  <BiDotsVerticalRounded className="tw-h-5 tw-w-5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <TaskOverflowContent align="end" className="tw-w-48">
                <TaskOverflowItem
                  className={`tw-cursor-pointer ${overflowDuplicateClass} ${running ? 'tw-pointer-events-none tw-opacity-50' : ''}`}
                  disabled={running}
                  onSelect={() => handleRunNow()}
                >
                  Run now
                </TaskOverflowItem>
                {onEditTask && (
                  <TaskOverflowItem
                    className={`tw-cursor-pointer ${overflowDuplicateClass}`}
                    onSelect={() => onEditTask(task)}
                  >
                    Edit
                  </TaskOverflowItem>
                )}
                {running && (
                  <TaskOverflowItem className={`tw-cursor-pointer ${overflowDuplicateClass}`} onSelect={() => handleStopRun()}>
                    Stop run
                  </TaskOverflowItem>
                )}
                <TaskOverflowItem
                  className="tw-cursor-pointer tw-text-red-600 focus:tw-text-red-600 focus:tw-bg-red-50"
                  disabled={deleting}
                  onSelect={() => handleDeleteClick()}
                >
                  Delete
                </TaskOverflowItem>
              </TaskOverflowContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-sky-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-sky-900 tw-ring-1 tw-ring-sky-100">
            {scheduleLabel(task)}
          </span>
          {isOnceTask(task) && formatOnceTaskTiming(task) && (
            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-violet-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-violet-900 tw-ring-1 tw-ring-violet-100">
              {formatOnceTaskTiming(task)}
            </span>
          )}
          {!isOnceTask(task) && (
            <span
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-pl-2.5 tw-pr-3 tw-py-0.5 tw-text-xs tw-font-medium ${
                task.is_active ? 'tw-bg-green-100 tw-text-green-800' : 'tw-bg-amber-100 tw-text-amber-900'
              }`}
            >
              <span
                className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${
                  task.is_active ? 'tw-bg-green-500' : 'tw-bg-amber-500'
                }`}
                aria-hidden
              />
              {task.is_active ? 'Active' : 'Paused'}
            </span>
          )}
          {isOnceTask(task) && (
            <span
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-pl-2.5 tw-pr-3 tw-py-0.5 tw-text-xs tw-font-medium ${
                onceTaskCompleted(task)
                  ? 'tw-bg-slate-100 tw-text-slate-700'
                  : task.is_active
                    ? 'tw-bg-violet-100 tw-text-violet-900'
                    : 'tw-bg-amber-100 tw-text-amber-900'
              }`}
            >
              <span
                className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${
                  onceTaskCompleted(task)
                    ? 'tw-bg-slate-400'
                    : task.is_active
                      ? 'tw-bg-violet-500'
                      : 'tw-bg-amber-500'
                }`}
                aria-hidden
              />
              {onceTaskStatusBadge(task).label}
            </span>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete scheduled task"
        message="This will remove the task from your list and stop it from running. Are you sure?"
        confirmText="Delete"
        isDangerous
      />

      {(task.description || task.instruction_sections) && (
        <div className="tw-border-b tw-border-slate-100 tw-px-3 tw-py-3 sm:tw-px-4">
          <TaskInstructionsCard
            text={task.description}
            document={task.instruction_sections}
          />
        </div>
      )}

      <div className="tw-flex-1 tw-overflow-y-auto tw-min-w-0 tw-px-3 tw-py-4 sm:tw-px-4">
        <RunHistorySection
          runs={runs}
          selectedRunId={selectedRunId}
          onSelectRun={setSelectedRunId}
          selectedRun={mergedSelectedRun}
          taskId={task.id}
          taskName={task.name}
          runsStatus={runsStatus}
          afterSummary={
            planSteps.length > 0 ? (
              <TaskPlanSteps steps={planSteps} collapsible defaultOpen={false}>
                {schedulePills}
              </TaskPlanSteps>
            ) : (
              schedulePills
            )
          }
        />
      </div>
    </div>
  )
}
